// File: frontend/src/views/private/Chat.js

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import websocketService from '../../services/websocketService';
import apiService from '../../services/apiService';
import MarkdownRenderer from '../../components/chat/MarkdownRenderer';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import { 
  ArrowLeft, 
  Send, 
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle
} from 'lucide-react';
import styles from './Chat.module.css';

const AGENT_SUGGESTIONS = {
  'ai_allegoryinsurance_policy_assistant': [
    "What are my coverage limits?",
    "What's my deductible?",
    "What exclusions apply?",
    "When does my policy renew?",
    "How do I file a claim?",
    "What's covered under liability?"
  ],
  'ai_allegoryinsurance_claims_assistant': [
    "How do I start a claim?",
    "What documents do I need?",
    "How long does a claim take?",
    "Is this incident covered?",
    "How do I check claim status?",
    "What's the claims process?"
  ],
  'ai_allegoryinsurance_quote_assistant': [
    "How much could I save?",
    "Show me a quote comparison",
    "What discounts am I eligible for?",
    "Can I get better coverage for less?",
    "Break down the premium calculation",
    "What's the market rate for my policy?"
  ],
  'ai_plato_general': [
    "Explain this concept simply",
    "Transform this for executives",
    "Make this more technical",
    "Add real-world examples",
    "Compare different perspectives",
    "Simplify this jargon"
  ],
  'ai_plato_property_and_casualty': [
    "Explain auto insurance coverage",
    "What is commercial liability?",
    "Compare homeowners vs renters",
    "Explain workers compensation",
    "What is cyber liability?",
    "Simplify underwriting concepts"
  ],
  'ai_ruleset_manager': [
    "Parse this underwriting rule",
    "Convert manual to ruleset",
    "Map fields to schema",
    "Validate rule structure",
    "Extract eligibility rules",
    "Generate deployment config"
  ],
  'ai_webcraft': [
    "I want to build a website",
    "How much does a website cost?",
    "Show me website examples",
    "What features can you build?",
    "How long does it take?",
    "Can you integrate with my tools?"
  ],
  'ai_actuary': [
    "Calculate mortality rates",
    "Analyze loss reserves",
    "Price insurance product",
    "Evaluate risk factors",
    "Run credibility analysis",
    "Model catastrophe losses"
  ]
};

const Chat = () => {
  const { chatId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [currentStreamingId, setCurrentStreamingId] = useState(null);
  const [error, setError] = useState(null);
  const [chatData, setChatData] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(true);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const hasLoadedHistory = useRef(false);
  
  // GUARD: Prevent concurrent initialization
  const isInitializing = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ 
      behavior: 'smooth',
      block: 'end'
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingMessage]);

  useEffect(() => {
    if (isInitializing.current) return;
    
    initializeChat();
    
    return () => {
      if (websocketService.isConnected()) {
        websocketService.disconnect();
      }
      isInitializing.current = false;
      hasLoadedHistory.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [inputValue]);

  const initializeChat = async () => {
    if (isInitializing.current) return;
    
    isInitializing.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiService.getChatHistory(chatId, user.user_id);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load chat');
      }

      setChatData(response.data.chat);

      if (response.data.messages && response.data.messages.length > 0) {
        const formattedMessages = response.data.messages.map(msg => ({
          id: msg.message_id,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp
        }));
        
        setMessages(formattedMessages);
        setShowSuggestions(false);
        hasLoadedHistory.current = true;
      } else {
        setMessages([{
          id: 'welcome',
          role: 'assistant',
          content: getWelcomeMessage(response.data.chat),
          timestamp: new Date().toISOString()
        }]);
        setShowSuggestions(true);
      }

      connectWebSocket();

    } catch (err) {
      console.error('[Chat] Failed to initialize:', err);
      setError(err.message || 'Failed to load chat');
      setIsLoading(false);
    } finally {
      isInitializing.current = false;
    }
  };

  const getWelcomeMessage = (chat) => {
    if (chat.agent_name === 'ai_allegoryinsurance_policy_assistant') {
      return `Hi! I've analyzed your insurance policy and I'm here to help you understand it better.\n\nI can answer questions about:\n\n• Coverage details and limits\n• Exclusions and restrictions\n• Premium breakdown\n• Deductibles and co-pays\n• Renewal terms\n\nWhat would you like to know?`;
    }
    if (chat.agent_name === 'ai_allegoryinsurance_claims_assistant') {
      return `Hi! I'm here to help you file a claim for your insurance policy.\n\nI'll guide you through:\n\n• Determining if your claim is covered\n• Gathering required documentation\n• Filling out claim forms\n• Understanding the claims process\n• Submitting to your insurer\n\nLet's start - what happened?`;
    }
    if (chat.agent_name === 'ai_allegoryinsurance_quote_assistant') {
      return `Hi! I've analyzed your current insurance policy and I'm ready to show you how Allegory Insurance can help you save.\n\n**Quick Preview:**\n• Your current premium: $${chat.policy_data?.premium_billing?.total_premium || 'analyzing...'}\n• Estimated Allegory range: Calculating...\n\nI can help you:\n\n• Get competitive premium estimates\n• Identify savings opportunities\n• Compare coverage options\n• Find eligible discounts\n• Optimize your coverage\n\nWhat would you like to explore first?`;
    }
    if (chat.agent_name === 'ai_plato_general') {
      return `Hi! I'm Plato, your AI knowledge assistant.\n\nI can help you:\n\n• Simplify complex concepts\n• Transform content for different audiences\n• Add technical depth or real-world examples\n• Compare different perspectives\n• Clarify jargon and terminology\n\nWhat would you like me to explain?`;
    }
    if (chat.agent_name === 'ai_plato_property_and_casualty') {
      return `Hi! I'm Plato, specialized in Property & Casualty insurance.\n\nI can explain:\n\n• Auto, home, and commercial insurance\n• Liability and workers compensation\n• Cyber insurance and emerging risks\n• Underwriting concepts and processes\n• Coverage comparisons and options\n\nWhat insurance topic can I help clarify?`;
    }
    if (chat.agent_name === 'ai_ruleset_manager') {
      return `Hi! I'm your Ruleset Manager assistant.\n\nI can help you:\n\n• Parse underwriting rules from documents\n• Convert manual guidelines to structured rulesets\n• Map fields to your schema\n• Validate rule structure and logic\n• Extract eligibility criteria\n• Generate deployment configurations\n\nWhat ruleset task can I assist with?`;
    }
    if (chat.agent_name === 'ai_webcraft') {
      return `Hi! I'm WebCraft, your website development assistant.\n\nI can help you:\n\n• Plan and design your website\n• Estimate costs and timelines\n• Show you examples and possibilities\n• Integrate with your existing tools\n• Build custom features\n• Deploy and maintain your site\n\nReady to build something amazing?`;
    }
    if (chat.agent_name === 'ai_actuary') {
      return `Hi! I'm Allegory Intelligence 1 (AI-1), your advanced actuarial analysis assistant.\n\nI specialize in:\n\n• Actuarial calculations and statistical analysis\n• Insurance pricing and risk assessment\n• Mortality, morbidity, and loss data analysis\n• Predictive modeling and validation\n• Regulatory compliance guidance\n• Loss reserving and ratemaking\n\nI can help with life insurance, property & casualty, health insurance, and enterprise risk management.\n\nWhat actuarial analysis can I help you with today?`;
    }
    return `Hi! I'm your AI assistant. How can I help you today?`;
  };

  const connectWebSocket = () => {
    if (websocketService.isConnected()) {
      setIsConnected(true);
      setIsLoading(false);
      return;
    }

    websocketService.connect(
      user.user_id,
      user.enterprise_id,
      () => {
        setIsConnected(true);
        setIsLoading(false);
      },
      (data) => {
        handleWebSocketMessage(data);
      },
      () => {
        setIsConnected(false);
      },
      (error) => {
        console.error('[Chat] WebSocket error:', error);
        setError('Connection lost. Please refresh.');
        setIsConnected(false);
      },
      true
    );
  };

  const handleWebSocketMessage = (data) => {
    if (data.message === 'Endpoint request timed out') return;

    if (data.type === 'stream_started') {
      setIsTyping(true);
      setStreamingMessage('');
      setCurrentStreamingId(data.message_id || `msg_${Date.now()}`);
      return;
    }

    if (data.type === 'stream_chunk') {
      setStreamingMessage(prev => prev + data.chunk);
      return;
    }

    if (data.type === 'stream_completed') {
      const finalContent = data.content || streamingMessage;
      
      if (finalContent && finalContent.trim()) {
        setMessages(prev => [...prev, {
          id: data.message_id || currentStreamingId || `msg_${Date.now()}`,
          role: 'assistant',
          content: finalContent,
          timestamp: data.timestamp || new Date().toISOString()
        }]);
      }
      
      setStreamingMessage('');
      setCurrentStreamingId(null);
      setIsTyping(false);
      setTimeout(() => scrollToBottom(), 50);
      return;
    }

    if (data.type === 'stream_error') {
      console.error('[Chat] Stream error:', data.error);
      setError(data.error || 'An error occurred');
      setIsTyping(false);
      setStreamingMessage('');
      setCurrentStreamingId(null);
      return;
    }
  };

  const sendMessage = (messageText = inputValue) => {
    if (!messageText.trim() || !chatId || !isConnected) return;
    if (!websocketService.isConnected()) {
      setIsConnected(false);
      return;
    }
  
    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: messageText.trim(),
      timestamp: new Date().toISOString()
    };
  
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setShowSuggestions(false);
    setTimeout(() => scrollToBottom(), 50);
  
    websocketService.send({
      action: 'sendMessage',
      data: {
        chat_id: chatId,
        message: userMessage.content,
        enterprise_id: user.enterprise_id
      }
    });
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleBack = () => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get('from');
    const fileId = params.get('fileId');

    if (from === 'policy-details' && fileId) {
      navigate(`/dashboard/policy-details/${fileId}`);
      return;
    }
    if (from === 'upload' && fileId) {
      navigate(`/dashboard/policy-details/${fileId}`);
      return;
    }
    if (from === 'claims') {
      navigate('/dashboard/claims');
      return;
    }
    if (from === 'ai') {
      navigate('/dashboard/ai');
      return;
    }
    if (from === 'chats') {
      navigate('/dashboard/chats');
      return;
    }
    if (chatData?.file_id) {
      navigate(`/dashboard/policy-details/${chatData.file_id}`);
      return;
    }
    navigate('/dashboard/policies');
  };

  const getAgentName = () => {
    if (chatData?.agent_name === 'ai_allegoryinsurance_policy_assistant') return 'Policy Assistant';
    if (chatData?.agent_name === 'ai_allegoryinsurance_claims_assistant') return 'Claims Assistant';
    if (chatData?.agent_name === 'ai_allegoryinsurance_quote_assistant') return 'Quote Assistant';
    if (chatData?.agent_name === 'ai_plato_general') return 'Plato - General';
    if (chatData?.agent_name === 'ai_plato_property_and_casualty') return 'Plato - P&C Insurance';
    if (chatData?.agent_name === 'ai_ruleset_manager') return 'Ruleset Manager';
    if (chatData?.agent_name === 'ai_webcraft') return 'WebCraft';
    if (chatData?.agent_name === 'ai_actuary') return 'Actuary AI';
    return 'AI Assistant';
  };
  
  const getAgentIcon = () => {
    if (chatData?.agent_name === 'ai_allegoryinsurance_policy_assistant') return '📋';
    if (chatData?.agent_name === 'ai_allegoryinsurance_claims_assistant') return '🏥';
    if (chatData?.agent_name === 'ai_allegoryinsurance_quote_assistant') return '💰';
    if (chatData?.agent_name === 'ai_plato_general') return '🎓';
    if (chatData?.agent_name === 'ai_plato_property_and_casualty') return '🏛️';
    if (chatData?.agent_name === 'ai_ruleset_manager') return '⚙️';
    if (chatData?.agent_name === 'ai_webcraft') return '🌐';
    if (chatData?.agent_name === 'ai_actuary') return '📊';
    return '🤖';
  };

  const getSuggestions = () => {
    if (!chatData?.agent_name) return [];
    return AGENT_SUGGESTIONS[chatData.agent_name] || [];
  };

  if (isLoading) {
    return (
      <div className={styles.chatPage}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
          <p className={styles.loadingText}>Loading chat...</p>
        </div>
      </div>
    );
  }

  if (error && !chatData) {
    return (
      <div className={styles.chatPage}>
        <div className={styles.errorContainer}>
          <AlertCircle size={48} className={styles.errorIcon} />
          <h2 className={styles.errorTitle}>Failed to Load Chat</h2>
          <p className={styles.errorMessage}>{error}</p>
          <button onClick={handleBack} className={styles.backButtonLarge}>
            <ArrowLeft size={20} strokeWidth={2} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatPage}>
      <div className={styles.chatHeader}>
        <button onClick={handleBack} className={styles.backButton}>
          <ArrowLeft size={20} strokeWidth={2} />
        </button>
        
        <div className={styles.headerInfo}>
          <span className={styles.agentIcon}>{getAgentIcon()}</span>
          <div className={styles.headerText}>
            <span className={styles.agentName}>{getAgentName()}</span>
            <span className={styles.connectionStatus}>
              {isConnected ? (
                <>
                  <CheckCircle size={12} strokeWidth={2} />
                  Connected
                </>
              ) : (
                <>
                  <XCircle size={12} strokeWidth={2} />
                  Disconnected
                </>
              )}
            </span>
          </div>
        </div>

        <div className={styles.headerSpacer} />
      </div>

      <div className={styles.messagesContainer} data-lenis-prevent>
        {error && (
          <div className={styles.errorBanner}>
            <AlertCircle size={16} strokeWidth={2} />
            <span>{error}</span>
            <button onClick={initializeChat} className={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {messages.map((msg) => (
          <div 
            key={msg.id} 
            className={`${styles.message} ${styles[msg.role]}`}
          >
            <div className={styles.messageContent}>
              {msg.role === 'assistant' ? (
                <MarkdownRenderer content={msg.content} />
              ) : (
                <p>{msg.content}</p>
              )}
            </div>
            <div className={styles.messageTime}>
              {new Date(msg.timestamp).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </div>
          </div>
        ))}

        {showSuggestions && !isTyping && messages.length <= 1 && (
          <div className={styles.suggestionsContainer}>
            <p className={styles.suggestionsLabel}>Try asking:</p>
            <div className={styles.suggestionBubbles}>
              {getSuggestions().map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={styles.suggestionBubble}
                  disabled={!isConnected}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {streamingMessage && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <div className={styles.messageContent}>
              <MarkdownRenderer content={streamingMessage} />
              <span className={styles.cursor}>▊</span>
            </div>
          </div>
        )}

        {isTyping && !streamingMessage && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <div className={styles.typingIndicator}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputContainer}>
        <div className={styles.inputWrapper}>
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isConnected ? "Ask me anything..." : "Connecting..."}
            className={styles.input}
            rows={1}
            disabled={!isConnected}
          />
          <button 
            onClick={() => sendMessage()}
            disabled={!inputValue.trim() || !isConnected}
            className={styles.sendButton}
            aria-label="Send message"
          >
            {isTyping ? (
              <Loader2 size={20} strokeWidth={2} className={styles.spinning} />
            ) : (
              <Send size={20} strokeWidth={2} />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;